import { Component, Suspense, useState, type ErrorInfo, type ReactNode } from "react";
import type { WidgetInstance } from "../../shared/layout";
import { isStaleChunk, reloadForNewVersion } from "../ui/ErrorBoundary";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";
import { getWidget } from "./registry";
import { chromeOf, resolveConfig, sizeVariant, type WidgetSize } from "./types";

/** Contains a widget crash to its own tile; the rest of the dashboard keeps running. */
class WidgetErrorBoundary extends Component<{ name: string; children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Widget "${this.props.name}" crashed`, error, info.componentStack);
    if (isStaleChunk(error)) reloadForNewVersion(error);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="empty-state">
          <Icon name="alert" size={26} />
          <strong>{this.props.name} hit a problem</strong>
          <button className="btn btn-sm" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function WidgetShell({
  item,
  width,
  height,
  editing,
  onConfigure,
  onRemove,
  onMove,
}: {
  item: WidgetInstance;
  width: number;
  height: number;
  editing: boolean;
  onConfigure?: () => void;
  onRemove?: () => void;
  /** Phone (stacked) editing: move up or down the list; null when not possible. */
  onMove?: { up: (() => void) | null; down: (() => void) | null };
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const def = getWidget(item.type);

  if (!def) {
    return (
      <div className="widget">
        <div className="empty-state">
          <Icon name="info" size={24} />
          <strong>Unknown widget</strong>
          <span>“{item.type}” isn’t available in this version.</span>
        </div>
        {editing && <EditOverlay name="Unknown widget" onRemove={onRemove} />}
      </div>
    );
  }

  const config = resolveConfig(def, item.config, item.version);
  const size: WidgetSize = { w: item.wall.w, h: item.wall.h, width, height, variant: sizeVariant(width, height) };
  const Body = def.Component;

  return (
    <section className={`widget widget-${def.type} variant-${size.variant} chrome-${chromeOf(def, config)}`} aria-label={def.name}>
      <WidgetErrorBoundary name={def.name}>
        <Suspense fallback={<div className="widget-loading" />}>
          <Body instanceId={item.id} config={config} size={size} editing={editing} fullscreen={false} openFullscreen={() => setFullscreen(true)} />
        </Suspense>
      </WidgetErrorBoundary>
      {editing && <EditOverlay name={def.name} onConfigure={def.settings.length ? onConfigure : undefined} onRemove={onRemove} onMove={onMove} />}
      {fullscreen && !editing && (
        <Sheet title={def.name} onClose={() => setFullscreen(false)} size="full">
          <WidgetErrorBoundary name={def.name}>
            <div className={`widget widget-${def.type} variant-expanded is-fullscreen`}>
              <Body
                instanceId={item.id}
                config={config}
                size={{ ...size, variant: "expanded", width: window.innerWidth, height: window.innerHeight - 80 }}
                editing={false}
                fullscreen
                openFullscreen={() => undefined}
              />
            </div>
          </WidgetErrorBoundary>
        </Sheet>
      )}
    </section>
  );
}

/** Edit-mode chrome: a big drag grip plus configure/remove buttons. Covers the widget so taps can't reach it. */
function EditOverlay({
  name,
  onConfigure,
  onRemove,
  onMove,
}: {
  name: string;
  onConfigure?: () => void;
  onRemove?: () => void;
  onMove?: { up: (() => void) | null; down: (() => void) | null };
}) {
  return (
    <div className="edit-overlay">
      {onMove ? (
        <>
          <div className="edit-move">
            <button className="icon-btn icon-btn-solid" onClick={onMove.up ?? undefined} disabled={!onMove.up} aria-label={`Move ${name} up`}>
              <Icon name="chevronDown" className="flip" />
            </button>
            <button className="icon-btn icon-btn-solid" onClick={onMove.down ?? undefined} disabled={!onMove.down} aria-label={`Move ${name} down`}>
              <Icon name="chevronDown" />
            </button>
          </div>
          <div className="edit-grip">
            <span>{name}</span>
          </div>
        </>
      ) : (
        <div className="edit-grip km-drag-handle" aria-label={`Drag to move ${name}`}>
          <Icon name="grip" size={26} />
          <span>{name}</span>
        </div>
      )}
      <div className="edit-actions">
        {onConfigure && (
          <button className="icon-btn icon-btn-solid" onClick={onConfigure} aria-label={`Configure ${name}`}>
            <Icon name="gear" />
          </button>
        )}
        {onRemove && (
          <button className="icon-btn icon-btn-solid danger" onClick={onRemove} aria-label={`Remove ${name}`}>
            <Icon name="trash" />
          </button>
        )}
      </div>
    </div>
  );
}
